import { type FC, type KeyboardEvent, type ReactNode, useContext } from "react";
import { Card, CardContent, Skeleton, Stack, Typography, useTheme } from "@mui/material";

import { OrdersHeader } from "@/views/production/orders/orders-header";
import { getMachineStatusColor } from "@/utils/machine-status-utils";
import { FailedRetry } from "@/components/failed-retry";
import { ProductionContext } from "@/contexts/production";
import { JobQueueContext } from "@/views/production/orders/job-queue-provider";
import { useShowMoreLess } from "@/hooks/use-show-more-less";
import { ShowMoreLessToggle } from "@/components/show-more-less-toggle";
import { useErrorsAndWarnings } from "@/hooks/use-errors-and-warnings";

import { MachineGroupStatusMessage } from "../machine-group-status-message/machine-group-status-message";
import { MachineGroupStatusIcon } from "../../components/machine-group-status";

import { InProductionOrder } from "./in-production-order";

interface InProductionOrdersCardContainerProps {
	readonly children: ReactNode;
}

const InProductionOrdersCardContainer: FC<InProductionOrdersCardContainerProps> = ({ children }) => {
	const { currentMachineGroup } = useContext(ProductionContext);
	const { openModal } = useErrorsAndWarnings();
	const isClickable = currentMachineGroup?.status === "Error";

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (isClickable === false || (event.key !== "Enter" && event.key !== " ")) {
			return;
		}

		event.preventDefault();
		openModal();
	};

	return (
		<Card
			onClick={isClickable ? openModal : undefined}
			onKeyDown={isClickable ? handleKeyDown : undefined}
			role={isClickable ? "button" : undefined}
			tabIndex={isClickable ? 0 : undefined}
			sx={isClickable ? { cursor: "pointer" } : undefined}
			data-testid="in-production-orders-card__container"
		>
			{children}
		</Card>
	);
};

export const InProductionOrdersCard: FC = () => {
	const { selectedBatches, batchesLoading, refetchEnabled } = useContext(JobQueueContext);
	const { currentMachineGroup } = useContext(ProductionContext);
	const {
		isExpanded,
		toggle,
		itemsToDisplay: batchesToDisplay,
		shouldShowToggle,
	} = useShowMoreLess({
		items: selectedBatches,
		threshold: 2,
	});

	const jobsInProgress = !batchesLoading && selectedBatches && selectedBatches.length > 0;
	const theme = useTheme();
	if (!refetchEnabled) {
		return <FailedRetry retry={() => window.location.reload()} />;
	}

	return (
		<InProductionOrdersCardContainer>
			<CardContent
				sx={{
					display: "grid",
					gridTemplateRows: "min-content 1fr min-content",
					gap: 1,
					"&:last-child": {
						paddingBottom: 1,
					},
					padding: 0,
					height: "100%",
					bgcolor:
						!jobsInProgress && currentMachineGroup
							? getMachineStatusColor({
									status: currentMachineGroup.status,
									productionEnabled: currentMachineGroup.productionEnabled,
									theme,
								})
							: undefined,
				}}
			>
				<OrdersHeader />
				{batchesLoading === true && (
					<Stack direction="column" gap={2} padding={2}>
						<Skeleton variant="rectangular" width="100%" height={64} sx={{ borderRadius: 1 }} />
						<Skeleton variant="rectangular" width="100%" height={64} sx={{ borderRadius: 1 }} />
					</Stack>
				)}
				{!batchesLoading && !jobsInProgress && currentMachineGroup ? (
					<Stack gap={2} sx={{ alignItems: "center", justifyContent: "center", flexGrow: 1 }}>
						<MachineGroupStatusIcon
							status={currentMachineGroup.status}
							productionEnabled={currentMachineGroup.productionEnabled}
							useColor={false}
							fontSize="large"
						/>
						<Typography variant="h4">
							<MachineGroupStatusMessage />
						</Typography>
					</Stack>
				) : null}
				{jobsInProgress === true && (
					<Stack direction="column" gap={2} padding={1}>
						{batchesToDisplay.map((batch) => (
							<InProductionOrder selectedBatch={batch} key={batch.batchId} />
						))}
					</Stack>
				)}
				{jobsInProgress === true && shouldShowToggle === true && (
					<ShowMoreLessToggle isExpanded={isExpanded} onToggle={toggle} />
				)}
			</CardContent>
		</InProductionOrdersCardContainer>
	);
};
